/**
 * /tracing-lightbox — painting. Two surfaces share these functions:
 *   - the light box itself (the whole screen under the paper), `paintLight`;
 *   - the plan on the page and the mini-map in the light box, `paintPlan`,
 *     which shows every screen-sized tile of a big picture at once.
 * Geometry comes from core.js; nothing here decides a size.
 */

const MARK = '#e11d74';          // registration crosses: a colour no drawing uses
const STRIP = 'rgba(225,29,116,0.10)';
const GRID = 'rgba(37,99,235,0.85)';

/**
 * The picture with its transform. pic = {img, srcW, srcH, cx, cy, s, a,
 * mirror, alpha}: centre in canvas px, `s` canvas px per source px, `a` deg.
 * The grid is drawn in the picture's own space, so it turns and mirrors with
 * it — the same N×N a person pencils onto the paper.
 */
export function paintPicture(ctx, pic, grid, gridPx) {
    if (!pic || !pic.img) return;
    ctx.save();
    ctx.translate(pic.cx, pic.cy);
    ctx.rotate((pic.a || 0) * Math.PI / 180);
    ctx.scale(pic.mirror ? -pic.s : pic.s, pic.s);
    const x0 = -pic.srcW / 2, y0 = -pic.srcH / 2;
    ctx.globalAlpha = pic.alpha == null ? 1 : pic.alpha;
    ctx.imageSmoothingEnabled = true;
    try { ctx.imageSmoothingQuality = 'high'; } catch (_) { /* old engines */ }
    ctx.drawImage(pic.img, x0, y0, pic.srcW, pic.srcH);
    ctx.globalAlpha = 1;
    if (grid > 1) {
        ctx.strokeStyle = GRID;
        ctx.lineWidth = (gridPx || 1.5) / pic.s;
        ctx.beginPath();
        for (let i = 1; i < grid; i++) {
            const x = x0 + pic.srcW * i / grid, y = y0 + pic.srcH * i / grid;
            ctx.moveTo(x, y0); ctx.lineTo(x, y0 + pic.srcH);
            ctx.moveTo(x0, y); ctx.lineTo(x0 + pic.srcW, y);
        }
        ctx.stroke();
        ctx.strokeRect(x0, y0, pic.srcW, pic.srcH);
    }
    ctx.restore();
}

/** A registration cross: two strokes and a ring, `r` px from the centre. */
export function paintMarks(ctx, marks, r, lw) {
    if (!marks || !marks.length) return;
    ctx.save();
    ctx.strokeStyle = MARK;
    ctx.lineWidth = lw || 2;
    ctx.beginPath();
    for (const m of marks) {
        ctx.moveTo(m.x - r, m.y); ctx.lineTo(m.x + r, m.y);
        ctx.moveTo(m.x, m.y - r); ctx.lineTo(m.x, m.y + r);
        ctx.moveTo(m.x + r * 0.45, m.y);
        ctx.arc(m.x, m.y, r * 0.45, 0, Math.PI * 2);
    }
    ctx.stroke();
    ctx.restore();
}

/** Tint the bands that come back on the neighbouring screen, dashed inner edge. */
export function paintStrips(ctx, strips, lw) {
    if (!strips || !strips.length) return;
    ctx.save();
    ctx.fillStyle = STRIP;
    for (const s of strips) ctx.fillRect(s.x, s.y, s.w, s.h);
    ctx.strokeStyle = 'rgba(225,29,116,0.45)';
    ctx.lineWidth = lw || 1;
    try { ctx.setLineDash([6, 6]); } catch (_) { /* old engines */ }
    ctx.beginPath();
    for (const s of strips) {
        if (s.side === 'left') { ctx.moveTo(s.x + s.w, s.y); ctx.lineTo(s.x + s.w, s.y + s.h); }
        if (s.side === 'right') { ctx.moveTo(s.x, s.y); ctx.lineTo(s.x, s.y + s.h); }
        if (s.side === 'top') { ctx.moveTo(s.x, s.y + s.h); ctx.lineTo(s.x + s.w, s.y + s.h); }
        if (s.side === 'bottom') { ctx.moveTo(s.x, s.y); ctx.lineTo(s.x + s.w, s.y); }
    }
    ctx.stroke();
    ctx.restore();
}

/**
 * The light box: white light, the picture, the tile's crosses and strips,
 * then the light level as a uniform dim (like the screen's own brightness).
 * scene = {W, H, dpr, light, pic, grid, gridPx, marks, markR, strips}
 */
export function paintLight(ctx, scene) {
    const { W, H, dpr } = scene;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    paintPicture(ctx, scene.pic, scene.grid, scene.gridPx);
    // over the picture, so the band that repeats on the next screen shows on a dark photo too
    paintStrips(ctx, scene.strips, 1.5);
    paintMarks(ctx, scene.marks, scene.markR || 14, 2);
    const lvl = scene.light == null ? 1 : scene.light;
    if (lvl < 0.999) {
        ctx.fillStyle = 'rgba(0,0,0,' + (1 - lvl).toFixed(3) + ')';
        ctx.fillRect(0, 0, W, H);
    }
}

/**
 * The plan: every screen (tile) as a lit rectangle, the picture across them,
 * what spills past the screens drawn faint, numbers, the current screen
 * outlined, finished ones ticked.
 * plan = {W, H, dpr, dark, screens:[{x,y,w,h,label,current,done}], pic, grid,
 *         numbers, frame}
 */
export function paintPlan(ctx, plan) {
    const { W, H, dpr } = plan;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const scr = plan.screens || [];
    // the light: union of the screens
    ctx.fillStyle = '#ffffff';
    for (const s of scr) ctx.fillRect(s.x, s.y, s.w, s.h);
    // what does not land on any screen, faint
    if (plan.pic) {
        paintPicture(ctx, { ...plan.pic, alpha: (plan.pic.alpha == null ? 1 : plan.pic.alpha) * 0.28 }, 0);
        ctx.save();
        ctx.beginPath();
        for (const s of scr) ctx.rect(s.x, s.y, s.w, s.h);
        ctx.clip();
        paintPicture(ctx, plan.pic, plan.grid, 1);
        ctx.restore();
    }
    if (scr.length > 1) {
        // overlaps show darker because each tile adds its own tint
        ctx.fillStyle = 'rgba(225,29,116,0.06)';
        for (const s of scr) ctx.fillRect(s.x, s.y, s.w, s.h);
    }
    ctx.lineWidth = 1;
    for (const s of scr) {
        ctx.strokeStyle = s.current ? '#2563eb' : (plan.dark ? 'rgba(148,163,184,0.9)' : 'rgba(71,85,105,0.8)');
        ctx.lineWidth = s.current ? 3 : 1;
        ctx.strokeRect(s.x + 0.5, s.y + 0.5, s.w - 1, s.h - 1);
    }
    if (scr.length > 1 && plan.numbers !== false) {
        const fs = Math.max(11, Math.min(22, Math.min(scr[0].w, scr[0].h) * 0.22));
        ctx.font = '700 ' + fs + 'px system-ui, -apple-system, "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (const s of scr) {
            const cx = s.x + s.w / 2, cy = s.y + s.h / 2;
            const txt = (s.done ? '✓ ' : '') + s.label;
            ctx.lineWidth = 4;
            ctx.strokeStyle = 'rgba(255,255,255,0.92)';
            ctx.strokeText(txt, cx, cy);
            ctx.fillStyle = s.current ? '#1d4ed8' : (s.done ? '#15803d' : '#334155');
            ctx.fillText(txt, cx, cy);
        }
    }
}
